import type { RefObject } from 'react';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { ChevronDownIcon } from '@utils/icons';
import styles from './StockEntryDialog.module.css';

interface IStockEntryComboProps {
  id: string;
  comboRef: RefObject<HTMLDivElement | null>;
  open: boolean;
  onBlurAway: () => void;
  value: string;
  className: string;
  /** The empty field's name. */
  placeholder?: string;
  /** Omit for a pick-only combo — the input turns read-only and only the
   *  list can set the value (the area field). Present, typing filters the
   *  options (the unit field). */
  onInputChange?: (value: string) => void;
  onInputClick: () => void;
  onCaretClick: () => void;
  /** `value` is the stable identity handed back on pick; `label` is what
   *  renders. Distinct so free-text labels (area names) can collide without
   *  the pick resolving to the wrong record. */
  options: Array<{ value: string; label: string }>;
  onPick: (value: string) => void;
}

/** The combo's caret — mouse-only (out of the tab order, hidden from AT);
 *  the input itself is the keyboard and screen-reader path. */
export function ComboCaret({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className={styles.caretBtn}
      tabIndex={-1}
      aria-hidden="true"
      onClick={onClick}
    >
      <ChevronDownIcon size={12} />
    </button>
  );
}

/**
 * The stock form's combo shell — input + caret + options list, dismissed by
 * `useComboDismiss`. One markup for every dropdown the form carries (unit,
 * area stored); the material field keeps its own combo because of its
 * add-new row and grouped suggestions.
 */
export function StockEntryCombo({
  id,
  comboRef,
  open,
  onBlurAway,
  value,
  className,
  placeholder,
  onInputChange,
  onInputClick,
  onCaretClick,
  options,
  onPick,
}: IStockEntryComboProps) {
  return (
    <div
      ref={comboRef}
      className={styles.combo}
      onBlur={(e) => {
        // Keyboard path (Tab away); pointer paths are handled by the
        // hook's document pointerdown listener.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onBlurAway();
      }}
    >
      <input
        id={id}
        className={className}
        value={value}
        readOnly={onInputChange === undefined}
        placeholder={placeholder}
        onChange={(e) => onInputChange?.(e.target.value)}
        onClick={onInputClick}
        autoComplete="off"
      />
      <ComboCaret onClick={onCaretClick} />
      {open && options.length > 0 ? (
        <div className={styles.comboList} role="listbox" onMouseDown={(e) => e.preventDefault()}>
          <ScrollArea>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={styles.comboOpt}
                onClick={() => onPick(option.value)}
              >
                {option.label}
              </button>
            ))}
          </ScrollArea>
        </div>
      ) : null}
    </div>
  );
}
